import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import styled from "styled-components";
import { AuthContext } from "../../context/AuthContext";
import { ADMIN_API_URL } from "../../utils/env";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";
import { makerRequestID } from ".";

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};
const APPLICATION_STATUSES = ["pending", "approved", "rejected"] as const;
const ApplicationOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  .application-dialog {
    background: #101016;
    border: 1px solid #30303b;
    border-radius: 12px;
    width: min(680px, 100%);
    max-height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 24px;
    box-shadow: 0 16px 64px #0008;
  }
  .application-dialog-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .application-dialog-heading h3 {
    margin: 0;
  }
  dl {
    margin: 24px 0;
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 16px;
    align-items: start;
  }
  dt {
    color: #a0a0ab;
    margin: 0;
    font-size: 13px;
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  dd p {
    margin-bottom: 0;
    font-size: 13px;
  }
  textarea {
    width: 100%;
    min-height: 96px;
    box-sizing: border-box;
  }
  @media (max-width: 600px) {
    padding: 8px;
    .application-dialog {
      padding: 16px;
    }
    dl {
      display: block;
    }
    dt {
      margin-top: 16px;
    }
    dd {
      margin-top: 6px;
    }
  }
`;

export interface MakerApplication {
  id: string;
  account: string;
  wallets: string[];
  markets: string[];
  hours: string;
  connectivity: string;
  contact: string;
  status: "pending" | "approved" | "rejected";
  revision: number;
  submitted_at: string;
  updated_at: string;
  review_note?: string;
}
export function verifyApplications(data: any): MakerApplication[] {
  if (
    !Array.isArray(data?.applications) ||
    data.applications.some(
      (a: any) =>
        !a ||
        ![a.id, a.account, a.hours, a.connectivity, a.contact].every(
          (v) => typeof v === "string"
        ) ||
        ![a.wallets, a.markets].every(
          (v) => Array.isArray(v) && v.every((x) => typeof x === "string")
        ) ||
        !["pending", "approved", "rejected"].includes(a.status) ||
        !Number.isInteger(a.revision) ||
        a.revision < 1 ||
        ![a.submitted_at, a.updated_at].every(
          (v) => typeof v === "string" && Number.isFinite(Date.parse(v))
        ) ||
        (a.review_note !== undefined && typeof a.review_note !== "string")
    )
  )
    throw new Error("Invalid applications response. Refresh to try again.");
  return data.applications;
}
interface ReviewRequest {
  request_id: string;
  application_id: string;
  expected_revision: number;
  status: "approved" | "rejected";
  note: string;
}
export function MakerApplications({
  serverTime,
  ready,
  onCount,
  onSetup,
  onRefreshState,
  initialApplicationID = "",
  active = true,
}: {
  serverTime?: string;
  ready: boolean;
  onCount: (count: number) => void;
  onSetup: (application: MakerApplication) => void;
  onRefreshState?: () => Promise<unknown>;
  initialApplicationID?: string;
  active?: boolean;
}) {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const list = useSWR(
    tokenId ? [ADMIN_API_URL("makers/applications"), tokenId] : null,
    async () => {
      const response = await axios.get(
        ADMIN_API_URL("makers/applications"),
        queryConfig()
      );
      if (response.status !== 200)
        throw new Error("Applications could not be loaded.");
      return verifyApplications(response.data);
    },
    { refreshInterval: 30000, shouldRetryOnError: false }
  );
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState<MakerApplication["status"][]>([
    "pending",
    "approved",
  ]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [selected, setSelected] = useState(initialApplicationID);
  const [decision, setDecision] = useState<ReviewRequest["status"]>();
  const [note, setNote] = useState("");
  const [reviewRevision, setReviewRevision] = useState(0);
  const [pending, setPending] = useState<ReviewRequest>();
  const [pendingApplication, setPendingApplication] =
    useState<MakerApplication>();
  const [refreshingState, setRefreshingState] = useState(false);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const currentApplication = list.data?.find((a) => a.id === selected);
  const application =
    currentApplication ||
    (pending?.application_id === pendingApplication?.id
      ? pendingApplication
      : undefined);
  const refreshServerState = async () => {
    if (!onRefreshState || refreshingState) return;
    setRefreshingState(true);
    try {
      await onRefreshState();
    } catch (e) {
      setError(affiliateError(e));
    } finally {
      setRefreshingState(false);
    }
  };
  useEffect(() => {
    onCount(list.data?.filter((a) => a.status === "pending").length || 0);
  }, [list.data, onCount]);
  const filteredApplications = (list.data || []).filter(
    (a) =>
      statuses.includes(a.status) &&
      [a.account, a.contact, ...a.wallets, ...a.markets].some((v) =>
        v.toLowerCase().includes(search.trim().toLowerCase())
      )
  );
  const closeDetails = () => {
    if (busy || pending) return;
    setSelected("");
    setDecision(undefined);
    setNote("");
    setError("");
    setSuccess("");
  };
  const applicationID = application?.id;
  useEffect(() => {
    if (!applicationID || !active) return undefined;
    const previous =
      triggerRef.current || (document.activeElement as HTMLElement);
    dialogRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [applicationID, active]);
  useEffect(() => {
    if (decision && active) {
      const input = document.getElementById("maker-application-note");
      input?.focus();
      input?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }
  }, [decision, active]);
  useEffect(() => {
    if (busy && active) dialogRef.current?.focus();
  }, [busy, active]);
  const submit = async () => {
    if (
      lock.current ||
      !ready ||
      !serverTime ||
      !application ||
      !decision ||
      !note.trim() ||
      list.error
    )
      return;
    const command = pending || {
      request_id: makerRequestID(serverTime),
      application_id: application.id,
      expected_revision: reviewRevision,
      status: decision,
      note: note.trim(),
    };
    lock.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    if (!pending) setPendingApplication(application);
    setPending(command);
    try {
      const response = await axios.post(
        ADMIN_API_URL("makers/applications/review"),
        command,
        { ...queryConfig(), timeout: 20000 }
      );
      if (
        response.status !== 200 ||
        response.data?.success !== true ||
        response.data?.request_id !== command.request_id
      )
        throw new Error("Review not confirmed. Retry the same decision.");
      const pollSaved = async (
        attempt: number
      ): Promise<MakerApplication | undefined> => {
        const refreshed = await list.mutate();
        const candidate = refreshed?.find(
          (a) => a.id === command.application_id
        );
        if (
          (candidate && candidate.revision > command.expected_revision) ||
          attempt === 7
        )
          return candidate;
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
        return pollSaved(attempt + 1);
      };
      const saved = await pollSaved(0);
      if (
        !saved ||
        saved.revision <= command.expected_revision ||
        saved.status !== command.status ||
        saved.review_note !== command.note
      )
        throw new Error(
          "Review not confirmed in saved state. Refresh or retry the same decision."
        );
      setPending(undefined);
      setDecision(undefined);
      setNote("");
      setSuccess(
        command.status === "approved"
          ? "Application approved. Set up membership separately to enroll this maker."
          : "Application rejected."
      );
    } catch (e) {
      setError(affiliateError(e));
      if ([400, 409, 422].includes((e as any)?.response?.status)) {
        setPending(undefined);
        setDecision(undefined);
        await list.mutate().catch(() => undefined);
      }
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const decisionButtonLabel = pending
    ? "Retry same decision"
    : "Confirm decision";
  return (
    <section aria-label="Maker applications">
      <div className="maker-section-heading">
        <div>
          <h2>Applications</h2>
          <p>Review requests, then set up an approved maker’s membership.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => list.mutate()}>
          Refresh applications
        </button>
      </div>
      <div
        className="maker-form-actions"
        style={{ flexWrap: "wrap", marginBottom: 16 }}
      >
        <label
          htmlFor="maker-application-search"
          style={{ flex: "1 1 260px", maxWidth: 420 }}
        >
          Find an application
          <input
            id="maker-application-search"
            type="search"
            style={{ width: "100%" }}
            placeholder="Wallet, market or contact"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend style={{ marginBottom: 8 }}>Application status</legend>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {APPLICATION_STATUSES.map((status) => (
              <label
                key={status}
                htmlFor={`maker-status-${status}`}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <input
                  id={`maker-status-${status}`}
                  type="checkbox"
                  checked={statuses.includes(status)}
                  onChange={(e) =>
                    setStatuses((values) =>
                      e.target.checked
                        ? [...values, status]
                        : values.filter((value) => value !== status)
                    )
                  }
                />
                {STATUS_LABELS[status]} (
                {list.data?.filter((a) => a.status === status).length || 0})
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      {list.error && (
        <p role="alert">
          {affiliateError(list.error)}{" "}
          <button type="button" onClick={() => list.mutate()}>
            Retry applications
          </button>
        </p>
      )}
      {list.data && selected && !application && (
        <p role="status">
          This application is no longer in the current list. It may have been
          replaced by a newer submission.
        </p>
      )}
      {error && !application && <p role="alert">{error}</p>}
      {!list.data && !list.error && (
        <p>
          {tokenId
            ? "Loading applications…"
            : "Sign in with your staff account."}
        </p>
      )}
      {list.data && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Account</th>
                <th>Markets</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((a) => (
                <tr key={a.id}>
                  <td style={{ overflowWrap: "anywhere" }}>{a.account}</td>
                  <td>{a.markets.join(", ")}</td>
                  <td>{a.contact}</td>
                  <td>{STATUS_LABELS[a.status]}</td>
                  <td>{new Date(a.submitted_at).toLocaleString()}</td>
                  <td>
                    <button
                      type="button"
                      disabled={busy || !!pending}
                      onClick={(e) => {
                        triggerRef.current = e.currentTarget;
                        setSelected(a.id);
                        setDecision(undefined);
                        setNote("");
                        setError("");
                        setSuccess("");
                      }}
                    >
                      View application
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredApplications.length === 0 && (
            <p>
              No matching applications.{" "}
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatuses(["pending", "approved"]);
                }}
              >
                Reset filters
              </button>
            </p>
          )}
        </div>
      )}
      {application && active && (
        <ApplicationOverlay
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDetails();
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- ARIA modal dialog handles Escape and traps keyboard focus. */}
          <div
            className="application-dialog"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="maker-application-title"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                closeDetails();
              }
              if (e.key !== "Tab") return;
              const controls = Array.from(
                e.currentTarget.querySelectorAll<HTMLElement>(
                  'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]'
                )
              );
              const first = controls[0];
              const last = controls[controls.length - 1];
              if (!first) {
                e.preventDefault();
                return;
              }
              if (
                e.shiftKey &&
                (document.activeElement === first ||
                  document.activeElement === e.currentTarget)
              ) {
                e.preventDefault();
                last.focus();
              } else if (
                !e.shiftKey &&
                (document.activeElement === last ||
                  document.activeElement === e.currentTarget)
              ) {
                e.preventDefault();
                first.focus();
              }
            }}
          >
            <div className="application-dialog-heading">
              <h3 id="maker-application-title">Application details</h3>
              <button
                type="button"
                disabled={busy || !!pending}
                onClick={closeDetails}
              >
                Close application
              </button>
            </div>
            {error && <p role="alert">{error}</p>}
            {success && <p role="status">{success}</p>}
            {list.error && (
              <p role="alert">
                Application data could not be refreshed. Retry before reviewing.{" "}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => list.mutate()}
                >
                  Retry applications
                </button>
              </p>
            )}
            {!ready && (
              <p role="alert">
                Server state is unavailable. Refresh it before continuing this
                review.{" "}
                {onRefreshState && (
                  <button
                    type="button"
                    disabled={busy || refreshingState}
                    onClick={refreshServerState}
                  >
                    {refreshingState
                      ? "Refreshing server state…"
                      : "Retry server state"}
                  </button>
                )}
              </p>
            )}
            {pending && list.data && !currentApplication && (
              <p role="alert">
                This application is no longer in the latest list. Your
                unconfirmed review is retained; retry it to check the saved
                result.{" "}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => list.mutate()}
                >
                  Refresh application list
                </button>
              </p>
            )}
            <dl>
              <dt>Signed-in account</dt>
              <dd>{application.account}</dd>
              <dt>Proposed wallets</dt>
              <dd>
                {application.wallets.join(", ")}
                <p>
                  Unverified proposals. Confirm wallet ownership and account
                  relationships before enrollment.
                </p>
              </dd>
              <dt>Markets</dt>
              <dd>{application.markets.join(", ")}</dd>
              <dt>Quoting hours (UTC)</dt>
              <dd>{application.hours || "24/7"}</dd>
              <dt>Connectivity</dt>
              <dd>{application.connectivity}</dd>
              <dt>Contact</dt>
              <dd>{application.contact}</dd>
              <dt>Status</dt>
              <dd>{STATUS_LABELS[application.status]}</dd>
              {application.review_note && (
                <>
                  <dt>Review note</dt>
                  <dd>{application.review_note}</dd>
                </>
              )}
            </dl>
            {application.status === "pending" && !decision && (
              <div className="maker-form-actions">
                <button
                  className="maker-primary"
                  type="button"
                  disabled={!ready || !!list.error}
                  onClick={() => {
                    setReviewRevision(application.revision);
                    setDecision("approved");
                  }}
                >
                  Approve application
                </button>
                <button
                  type="button"
                  disabled={!ready || !!list.error}
                  onClick={() => {
                    setReviewRevision(application.revision);
                    setDecision("rejected");
                  }}
                >
                  Reject application
                </button>
              </div>
            )}
            {decision && (
              <div>
                <h3>
                  {decision === "approved"
                    ? "Confirm approval"
                    : "Confirm rejection"}
                </h3>
                <p>
                  {decision === "approved"
                    ? "Approval does not enroll this maker or change fees. Membership requires a separate reviewed agreement."
                    : "The applicant will see this decision and your note."}
                </p>
                <label htmlFor="maker-application-note">
                  Note to applicant
                  <textarea
                    id="maker-application-note"
                    maxLength={2000}
                    required
                    disabled={busy || !!pending}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                <div className="maker-form-actions" style={{ marginTop: 12 }}>
                  <button
                    className="maker-primary"
                    type="button"
                    disabled={!ready || busy || !!list.error || !note.trim()}
                    onClick={submit}
                  >
                    {busy ? "Saving decision…" : decisionButtonLabel}
                  </button>
                  <button
                    type="button"
                    disabled={busy || !!pending}
                    onClick={() => {
                      setDecision(undefined);
                      setNote("");
                    }}
                  >
                    Cancel review
                  </button>
                </div>
                {pending && !busy && (
                  <p>
                    The previous response is unconfirmed. Retrying uses the same
                    request ID.
                  </p>
                )}
              </div>
            )}
            {application.status === "approved" && (
              <button
                type="button"
                className="maker-primary"
                disabled={!ready || !!list.error || !!pending}
                onClick={() => {
                  closeDetails();
                  onSetup(application);
                }}
              >
                Set up membership
              </button>
            )}
          </div>
        </ApplicationOverlay>
      )}
    </section>
  );
}
